import Decimal from 'decimal.js-light';
import { formatUnits } from 'viem';

import { normalizeChainIdFor0xApi } from '@/lib/api/0x-cross-chain/constants';
import { getCrossChainGasFee } from '@/lib/api/0x-cross-chain/gasCosts';
import type { CrossChainQuote } from '@/lib/api/0x-cross-chain/types';

import { addConvertedAmounts, calculateExchangeRate } from '@/components/TransactionModule/utils';

import { PLACEHOLDER_ADDRESS_EVM, PLACEHOLDER_ADDRESS_SOLANA } from '@/constants/addresses';
import { solana } from '@/constants/chain';

import type { CrossChainQuoteParams } from '@/hooks/0xCrossChain/useQuotes';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { DYNAMIC_ROUTING_THRESHOLD } from '@/utils/crossChain';
import { crossChainGasEstimates, FEE_BUFFER } from '@/utils/fees';
import { ChainAddress } from '@/utils/models';
import { isFeeInToken } from '@/utils/quote-fees';

/**
 * Get the amount in base units from a decimal input.
 */
export const getAmount = (
  input: { decimal: string },
  decimals: number | undefined,
): string | undefined => {
  if (!input.decimal || typeof decimals !== 'number' || decimals < 0) return undefined;
  try {
    const decimal = new Decimal(input.decimal);
    return decimal.mul(new Decimal(10).pow(decimals)).toFixed(0, Decimal.ROUND_DOWN);
  } catch {
    return undefined;
  }
};

/**
 * Convert chain ID to 0x API format (Solana uses 'solana' or numeric).
 * Delegates to {@link normalizeChainIdFor0xApi} for the actual conversion.
 */
const toCrossChainApiChainId = (chainId: number): number | string => {
  return normalizeChainIdFor0xApi(chainId);
};

/**
 * Build quote parameters for the 0x cross-chain API.
 
 * Trades above $10,000  are sorted by price, 
 * and smaller trades are sorted by speed.
 */
const getPlaceholderAddress = (chainId: number): ChainAddress =>
  chainId === solana.id
    ? (PLACEHOLDER_ADDRESS_SOLANA as unknown as ChainAddress)
    : (PLACEHOLDER_ADDRESS_EVM as unknown as ChainAddress);

export const getQuoteParams = (
  sellToken: TokenResult | undefined,
  buyToken: TokenResult | undefined,
  sellAmount: string | undefined,
  sellSideWalletAddress: ChainAddress | undefined,
  buySideWalletAddress: ChainAddress | undefined,
  slippageBps: number,
  slippageMode: CrossChainQuoteParams['slippageMode'],
  sellAmountUsd: number | undefined,
): CrossChainQuoteParams | undefined => {
  if (!sellToken || !buyToken || !sellAmount || sellToken.chainId === buyToken.chainId) {
    return undefined;
  }

  const sortQuotesBy: 'price' | 'speed' =
    sellAmountUsd !== undefined && sellAmountUsd >= DYNAMIC_ROUTING_THRESHOLD ? 'price' : 'speed';

  return {
    originChain: toCrossChainApiChainId(sellToken.chainId),
    destinationChain: toCrossChainApiChainId(buyToken.chainId),
    sellToken: sellToken.address,
    buyToken: buyToken.address,
    sellAmount,
    originAddress: sellSideWalletAddress ?? getPlaceholderAddress(sellToken.chainId),
    destinationAddress: buySideWalletAddress ?? getPlaceholderAddress(buyToken.chainId),
    slippageBps,
    slippageMode,
    sortQuotesBy,
  };
};

/**
 * Calculates gross buy amount for cross-chain quotes by adding fees back
 * into the net buy amount returned by the API.
 */
export const calculateGrossBuyAmountCrossChain = ({
  quote,
  sellToken,
  buyToken,
}: {
  quote: CrossChainQuote;
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
}): string => {
  if (!sellToken || !buyToken) {
    return quote.buyAmount;
  }

  const { zeroExFee, integratorFee } = quote.fees;
  if (!zeroExFee && !integratorFee) {
    return quote.buyAmount;
  }

  const isZeroExFeeInSellToken = isFeeInToken(zeroExFee?.token, sellToken);
  const isZeroExFeeInBuyToken = isFeeInToken(zeroExFee?.token, buyToken);
  const isIntegratorFeeInSellToken = isFeeInToken(integratorFee?.token, sellToken);
  const isIntegratorFeeInBuyToken = isFeeInToken(integratorFee?.token, buyToken);

  const totalSellTokenFeeAmount = new Decimal(0)
    .add(isZeroExFeeInSellToken ? zeroExFee?.amount || '0' : '0')
    .add(isIntegratorFeeInSellToken ? integratorFee?.amount || '0' : '0');
  const totalBuyTokenFeeAmount = new Decimal(0)
    .add(isZeroExFeeInBuyToken ? zeroExFee?.amount || '0' : '0')
    .add(isIntegratorFeeInBuyToken ? integratorFee?.amount || '0' : '0');

  const exchangeRate = calculateExchangeRate({
    sellAmount: new Decimal(quote.sellAmount).sub(totalSellTokenFeeAmount).toString(),
    buyAmount: new Decimal(quote.buyAmount).add(totalBuyTokenFeeAmount).toString(),
    sellDecimals: sellToken.decimals,
    buyDecimals: buyToken.decimals,
  });

  const zeroExFeeInBuyToken = isZeroExFeeInSellToken
    ? addConvertedAmounts({
        amount1: zeroExFee?.amount || '0',
        decimals1: sellToken.decimals,
        amount2: '0',
        decimals2: buyToken.decimals,
        outputDecimals: buyToken.decimals,
        exchangeRate,
      })
    : isZeroExFeeInBuyToken
      ? zeroExFee?.amount || '0'
      : '0';
  const integratorFeeInBuyToken = isIntegratorFeeInSellToken
    ? addConvertedAmounts({
        amount1: integratorFee?.amount || '0',
        decimals1: sellToken.decimals,
        amount2: '0',
        decimals2: buyToken.decimals,
        outputDecimals: buyToken.decimals,
        exchangeRate,
      })
    : isIntegratorFeeInBuyToken
      ? integratorFee?.amount || '0'
      : '0';

  const totalFeesInBuyToken = addConvertedAmounts({
    amount1: zeroExFeeInBuyToken,
    decimals1: buyToken.decimals,
    amount2: integratorFeeInBuyToken,
    decimals2: buyToken.decimals,
    outputDecimals: buyToken.decimals,
    exchangeRate: new Decimal(1),
  });

  return new Decimal(quote.buyAmount).add(totalFeesInBuyToken).toFixed(0, Decimal.ROUND_HALF_EVEN);
};

/**
 * Check if the user's native balance can cover network fees.
 * Uses the actual gas costs from the quote instead of a static estimate.
 */
export const getCanBalanceCoverNetworkFees = (
  quote: CrossChainQuote | undefined,
  sellToken: TokenResult | undefined,
  evmBalance: bigint | undefined,
  solBalance: bigint | undefined,
): boolean => {
  if (!quote || !sellToken) return true;

  const gasFee = getCrossChainGasFee(quote.gasCosts);
  const bridgeFee = BigInt(quote.fees.bridgeNativeFee?.amount ?? '0');
  const totalNativeFee = gasFee + bridgeFee;
  if (totalNativeFee === 0n) return true;

  if (sellToken.chainId === solana.id) {
    if (solBalance === undefined) return false;
    return solBalance >= totalNativeFee;
  } else {
    if (evmBalance === undefined) return true;
    return evmBalance >= totalNativeFee;
  }
};

/**
 * Get the network fee reserve (in human-readable native-token units) to
 * subtract from a native-token sell balance when the user taps 50 % / Max.
 *
 * Warm path: use the quote's origin-chain gas fee × 1.2 buffer.
 * Cold path: fall back to the static `crossChainGasEstimates` table.
 */
export const getNetworkFeeReserve = (
  quote: CrossChainQuote | undefined,
  sellToken: TokenResult | undefined,
): number => {
  if (quote && sellToken) {
    const gasFee = getCrossChainGasFee(quote.gasCosts);
    const bridgeFee = BigInt(quote.fees.bridgeNativeFee?.amount ?? '0');
    const totalFee = gasFee + bridgeFee;
    if (totalFee > 0n) {
      const feeInNativeUnits = Number(formatUnits(totalFee, sellToken.decimals));
      return feeInNativeUnits * FEE_BUFFER;
    }
  }

  // No quote yet — use the static per-chain estimate
  return sellToken ? (crossChainGasEstimates[sellToken.chainId] ?? 0) : 0;
};

/**
 * Check if approval is required from the quote issues.
 */
export const isApprovalRequired = (quote: CrossChainQuote | undefined): boolean => {
  return !!quote?.issues?.allowance;
};
